import React, { useContext, useEffect, useRef, useState } from 'react';
import { styled } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import Link from 'next/link';
import _ from 'lodash';
import { Data, post } from '../../networking';
import SessionContext from '../../contexts/session';
import { Box, Button, CircularProgress, TextField, Typography } from '@mui/material';
import { ampLogEvent } from '../../lib/amplitude';
import { refreshUserAndStoreIn } from '../../logic/auth';
import { useRouter } from 'next/router';
import SunnysideButton from '../../components_sunnyside/button';
import { colors } from '../../styles/_colors';
import { useCookies } from 'react-cookie';
import dataDogLogger from '../../lib/datadog';

async function postAuthLoginPhone(payload: Data): Promise<Data> {
    return await post('auth/login/phone', payload);
}

const postTwilioVerify = async (phone, voiceCall = false): Promise<Data> => {
    let url = 'twilio/verify';
    if (voiceCall) {
        url += '/voice';
    }
    return await post(url, { phone });
};

const postTwilioValidateAndFinalize = async (phone, code, formId, mode): Promise<Data> => {
    const result = await postTwilioValidate(phone, code, mode);
    /* Do not finalize diagnostic when code validation fails */
    if (result.message) {
        return result;
    }
    await post('diagnostic/finalize', {
        formId: formId || ''
    });
    return result;
};

const postTwilioValidate = async (phone, code, mode): Promise<Data> => {
    const result = await post('twilio/validate', { phone, code, mode });
    return result;
};

const StyledLink = styled('a')(({ theme }) => ({
    cursor: 'pointer',
    fontWeight: 'bold',
    [theme.breakpoints.down('sm')]: {
        fontSize: '1rem',
        lineHeight: '1.25rem',
    },
    [theme.breakpoints.up('sm')]: {
        fontSize: '1.25rem',
        lineHeight: '2rem',
    },
}));

type Props = {
    mode: 'register' | 'login' | 'non-specific',
    phone?: string,
    submitButtonLabel?: string,
    autoSubmit?: boolean,
    onChange?: (data) => void,
    onClickUpdatePhone?: () => void,
    persistentLogin?: boolean,
    isCustom?: boolean,
    hideText?: boolean,
    formId?: string,
    goBack?: () => void,
};

export default function Code(props: Props): JSX.Element {
    const useStyles = makeStyles((theme) => ({
        paper: {
            marginTop: theme.spacing(8),
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
        },
        avatar: {
            margin: theme.spacing(1),
            backgroundColor: theme.palette.secondary.main,
        },
        form: {
            marginTop: theme.spacing(1),
        },
        submit: {
            margin: theme.spacing(3, 0, 2),
        },
        input: {
            [theme.breakpoints.down('sm')]: {
                width: '100%',
            },
            [theme.breakpoints.up('sm')]: {
                width: props.isCustom ? '100%' : '21rem',
            },
            '& input': {
                fontSize: '1rem',
                lineHeight: '1.375rem',
                textAlign: 'center',
                background: '#F9F9F9',
                boxShadow: 'inset 4px 4px 5px rgba(0, 0, 0, 0.05)',
                [theme.breakpoints.down('sm')]: {
                    width: '100%',
                },
                [theme.breakpoints.up('sm')]: {
                    width: props.isCustom ? '100%' : '21rem',
                },
            }
        },
        inputError: {
            marginTop: '1rem',
            [theme.breakpoints.down('sm')]: {
                width: '100%',
                padding: props.isCustom ? '0' : '0 1rem'
            },
            [theme.breakpoints.up('sm')]: {
                width: '21rem'
            }
        },
        loadingContainer: {
            position: 'absolute',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            top: 10,
            zIndex: 100
        },
        arrowButton: {
            '&:hover': {
                backgroundColor: '#FFE635',
            },
            backgroundColor: '#FFE635',
            padding: '16px',
            borderRadius: '50%',
            placeSelf: 'end',
            minWidth: 0,
            lineHeight: 0,
            marginTop: 'auto',
            transition: 'opacity 0.5s, pointerEvents 0.5s'
        }
    }));

    const [cookies] = useCookies();
    const [code, setCode] = useState('');
    const [error, setError] = useState('');
    const [verifyCodeError, setVerifyCodeError] = useState(false);
    const [submitInFlight, setSubmitInFlight] = useState(false);
    const [sentCode, setSentCode] = useState(false);
    const timerRef = useRef(null);
    const auto = typeof props.autoSubmit === 'undefined' ? true : props.autoSubmit;
    const router = useRouter();

    const [renderInput, setRenderInput] = useState(false);
    useEffect(() => {
        setTimeout(() => {
            setRenderInput(true);
        }, 1000);
    }, []);

    const session = useContext(SessionContext);
    const userPhone = props.phone || session.user.phone;

    const classes = useStyles();

    const submitButtonText = props.mode === 'login' ? 'Log in' : props.submitButtonLabel || 'Verify phone';

    const sendTwillioVerifyRequest = async (voiceCall = false): Promise<void> => {
        setVerifyCodeError(false);
        /**
         * Code prevents rate limit issues. MUST be before request incase request is slow to prevent
         * users from repeatedly requesting code when request is still processing
         */
        setSentCode(true);
        timerRef.current = setTimeout(() => setSentCode(false), 15000);
        const twillioResponse = await postTwilioVerify(userPhone, voiceCall);
        if (twillioResponse.message) {
            // Too many codes sent in short time error
            if (twillioResponse.statusCode === 400) {
                setError(twillioResponse.message as string);
            } else {
                setVerifyCodeError(true);
            }
        }
    };

    useEffect(() => {
        return () => clearTimeout(timerRef.current);
    }, []);

    useEffect(() => {
        if (userPhone) {
            sendTwillioVerifyRequest();
            ampLogEvent(
                'signup: sent auth sms',
                (props.isCustom ? { formId: props.formId } : {}),
                cookies
            );
        }
        setSubmitInFlight(false);
        setError('');
    }, [userPhone]);

    const handleSubmitClick = async (e) => {
        if (e) {
            e.preventDefault();
        }
        ampLogEvent(
            'signup: clicked authorize phone number',
            (props.isCustom ? { formId: props.formId } : {}),
            cookies
        );
        await handleTwilioValidateRequest();
    };

    const sendTwillioValidateRequest = async () => {
        if (submitInFlight) return;

        dataDogLogger('info', '[auth-code] validation request started', { rawData: { userPhone } });
        setSubmitInFlight(true);
        let validateResponse;
        if (props.mode === 'register') {
            validateResponse = await postTwilioValidateAndFinalize(
                userPhone, 
                code, 
                props.isCustom ? props.formId : router.query.formId,
                props.mode
            );
        } else if (props.mode === 'login') {
            validateResponse = await postAuthLoginPhone({
                phone: userPhone,
                code: code,
                persistent: props.persistentLogin
            });
        } else if (props.mode === 'non-specific') {
            validateResponse = await postTwilioValidate(
                userPhone,
                code,
                props.mode
            );
        }
        dataDogLogger(
            'info',
            '[auth-code] validation request',
            { rawData: { validateResponse, userPhone } }
        );
        if (validateResponse.message) {
            setError(validateResponse.message as string);
            setSubmitInFlight(false);
            await props.onChange(false);
            dataDogLogger(
                'info',
                '[auth-code] validation request failed',
                { rawData: { validateResponse, userPhone } }
            );
            return validateResponse.message;
        }
        dataDogLogger(
            'info',
            '[auth-code] validation request succeeded',
            { rawData: { validateResponse, userPhone } }
        );
        await refreshUserAndStoreIn(session);
        if (props.onChange) {
            await props.onChange(true);
        }
    };

    const handleTwilioValidateRequest = _.debounce(sendTwillioValidateRequest, 1000, {
        leading: true,
        trailing: false
    });

    const onChange = (e) => {
        const value = e.target.value;
        setCode(value);
    };

    /* Used since `maxLength` doesn't work with number inputs */
    const onInput = (e) => {
        e.target.value = e.target.value.slice(0, 6);
    };

    useEffect(() => {
        if (code.length === 6 && auto) {
            handleTwilioValidateRequest();
        }
    }, [code]);

    const onResendCodeClick = () => {
        sendTwillioVerifyRequest();
    };

    const onReceiveCallClick = () => {
        sendTwillioVerifyRequest(true);
    };

    return (
        <Box
            style={{
                display: props.isCustom ? 'flex' : 'auto',
                flexDirection: 'column',
                height: props.isCustom ? '100%' : 'auto'
            }}
        >
            <Box
                mt={props.isCustom ? '0' : '2em'}
                mb={props.mode === 'register' ? props.isCustom ? '40px' : '2rem' : '9rem'}
                position="relative"
                display='flex'
                flexDirection='column'
                alignItems='center'
                justifyContent='center'
            >
                {props.isCustom && !props.hideText && (
                    <>
                        <Typography
                            fontFamily={'Poppins'}
                            fontSize={'14px !important'}
                            lineHeight={'20px !important'}
                            color={colors.dark_gray}
                            width={'100%'}
                        >
                            {`We sent the code to ${userPhone}`}
                        </Typography>
                        <Typography
                            fontFamily={'Poppins'}
                            fontSize={'14px !important'}
                            lineHeight={'20px !important'}
                            color={colors.sky}
                            width={'100%'}
                            onClick={props.goBack}
                            style={{ cursor: 'pointer' }}
                            mb={'30px'}
                        >
                            Update phone number
                        </Typography>
                    </>
                )}
                {props.isCustom && !renderInput && (
                    <form noValidate onSubmit={handleSubmitClick} style={{ width: props.isCustom ? '100%' : 'auto' }}>
                        {submitInFlight && (
                            <Box className={classes.loadingContainer}>
                                <Box width="12rem">
                                    <CircularProgress />
                                </Box>
                            </Box>
                        )}
                        <TextField
                            id="digitCode"
                            onChange={onChange}
                            disabled={submitInFlight}
                            className={classes.input}
                            autoComplete="one-time-code"
                            autoFocus={false}
                            inputProps={{ inputMode: 'numeric' }}
                            onInput={onInput}
                            placeholder="Enter your 6-digit code here"
                            required={true}
                            type="number"
                        />
                    </form>
                )}
                {(!props.isCustom || renderInput) && (
                    <form noValidate onSubmit={handleSubmitClick} style={{ width: props.isCustom ? '100%' : 'auto' }}>
                        {submitInFlight && (
                            <Box className={classes.loadingContainer}>
                                <Box width="12rem">
                                    <CircularProgress />
                                </Box>
                            </Box>
                        )}
                        <TextField
                            id="digitCode"
                            onChange={onChange}
                            disabled={submitInFlight}
                            className={classes.input}
                            autoComplete="one-time-code"
                            autoFocus={true}
                            inputProps={{ inputMode: 'numeric' }}
                            onInput={onInput}
                            placeholder="Enter your 6-digit code here"
                            required={true}
                            type="number"
                        />
                    </form>
                )}
                {verifyCodeError && (
                    <Box mt="1rem">
                        <Typography color="error">
                            We ran into a snag sending your code.
                            <br></br>Click below to resend your code.
                        </Typography>
                    </Box>
                )}
                {error && (
                    <Box className={classes.inputError}>
                        <Typography
                            fontFamily={'Poppins'}
                            fontSize={props.isCustom ? '16px !important' : ''}
                            lineHeight={props.isCustom ? '24px !important' : ''}
                            textAlign={props.isCustom ? 'center' : 'left'}
                            color="error"
                        >
                            {error}
                        </Typography>
                    </Box>
                )}
            </Box>
            {!auto && !props.isCustom && (
                <SunnysideButton
                    fullWidth
                    className={classes.submit}
                    borderRadius="10px"
                    disabled={code.length < 6 || submitInFlight}
                    onClick={handleSubmitClick}
                    fontSize='1.1rem'
                >
                    {submitButtonText}
                </SunnysideButton>
            )}
            <Box
                mt={props.isCustom ? 0 : '1rem'}
                display='flex'
                flexDirection='column'
                alignItems='center'
            >
                {props.isCustom ? (
                    <>
                        <Typography
                            fontFamily={'Poppins'}
                            fontSize={'14px !important'}
                            lineHeight={'20px !important'}
                            color={'#767676'}
                            width={'100%'}
                        >
                            {'Didn\'t receive it? We sent the code. Please wait 15 seconds to resend.'}
                        </Typography>
                        {!sentCode && (
                            <>
                                <Typography
                                    fontFamily={'Poppins'}
                                    fontSize={'14px !important'}
                                    lineHeight={'20px !important'}
                                    color={colors.sky}
                                    width={'100%'}
                                    onClick={onResendCodeClick}
                                    mt={'16px'}
                                >
                                    Resend SMS code
                                </Typography>
                                <Typography
                                    fontFamily={'Poppins'}
                                    fontSize={'14px !important'}
                                    lineHeight={'20px !important'}
                                    color={colors.sky}
                                    width={'100%'}
                                    onClick={onReceiveCallClick}
                                    mt={'16px'}
                                >
                                    Receive code by phone call
                                </Typography>
                            </>
                        )}
                    </>
                ) : (
                    <>
                        <Typography
                            variant='body2'
                            color={colors.light_gray}
                            textAlign='center'
                            width='18rem'
                            padding='0.5rem 0'
                        >
                            We sent the code to {userPhone}
                        </Typography>
                        <Typography paddingBottom='2.5rem'>
                            {(props.mode === 'register')
                                ?
                                <Link href='/register/phone?updatePhone=true'>
                                    Update phone number
                                </Link> :
                                <StyledLink
                                    onClick={props.onClickUpdatePhone}
                                >
                                    Update phone number
                                </StyledLink>}
                        </Typography>
                        <Typography
                            variant='body2'
                            color={colors.light_gray}
                            paddingBottom='0.5rem'
                        >
                            Didn&#39;t receive it?
                        </Typography>
                        {sentCode ?
                            <Typography variant='body2'>
                                We sent the code. Please wait 15 seconds to resend.
                            </Typography> :
                            <Box>
                                <Box>
                                    <StyledLink
                                        onClick={onResendCodeClick}
                                    >
                                        Resend SMS code
                                    </StyledLink>
                                </Box>
                                <Box>
                                    <Typography> - OR - </Typography>
                                </Box>
                                <Box>
                                    <StyledLink
                                        onClick={onReceiveCallClick}
                                    >
                                        Receive code by phone call
                                    </StyledLink>
                                </Box>
                            </Box>
                        }
                    </>
                )}
            </Box>
            {!auto && props.isCustom && (
                <Button className={classes.arrowButton} style={{ marginTop: 'auto', opacity: code.length >= 6 ? 1 : 0, pointerEvents: (code.length < 6 || submitInFlight) ? 'none' : 'auto' }} onClick={handleSubmitClick}>
                    <svg width="16" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8.70503 14.095C8.43166 13.8216 8.43166 13.3784 8.70503 13.105L13.1101 8.69999L2.2 8.69999C1.8134 8.69999 1.5 8.38659 1.5 7.99999C1.5 7.61339 1.8134 7.29999 2.2 7.29999L13.1101 7.29999L8.70503 2.89496C8.43166 2.62159 8.43166 2.17838 8.70503 1.90501C8.97839 1.63164 9.42161 1.63164 9.69498 1.90501L15.295 7.50501C15.5683 7.77838 15.5683 8.22159 15.295 8.49496L9.69497 14.095C9.42161 14.3683 8.97839 14.3683 8.70503 14.095Z" fill={'black'} />
                    </svg>
                </Button>
            )}
        </Box>
    );
}
